<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Redeem Produk"
    :show-banner="false"
    :show-sponsors="false"
    :show-back-button="true"
    :hide-sidebar="true"
    @back="goBack"
  >

    <!-- Points Header -->
    <div class="points-header">
      <div class="points-display">
        <i class="mdi mdi-star points-icon"></i>
        <span class="points-text">Poin Anda: {{ userPoints.point || 0 }}</span>
      </div>
      <bert-button 
        variant="outline" 
        size="sm" 
        @click="goToHistory"
        class="history-btn"
      >
        Riwayat Redeem →
      </bert-button>
    </div>

    <!-- Exchange Availability Notice -->
    <div v-if="!isExchangeAvailable" class="exchange-notice">
      <div class="notice-content">
        <i class="mdi mdi-information notice-icon"></i>
        <div class="notice-text">
          <h4>Penukaran Tidak Tersedia</h4>
          <p>Penukaran produk hanya tersedia pada hari: <strong>{{ getExchangeDays() }}</strong></p>
        </div>
      </div>
    </div>

    <!-- Product Selection (when exchange is available) -->
    <div v-if="isExchangeAvailable && !showForm" class="products-section">
      <div class="section-header">
        <h3 class="section-title">Pilih Produk</h3>
        <p class="section-subtitle">Tukar poinmu dengan produk di bawah ini!</p>
      </div>

      <div v-if="products.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="mdi mdi-package-variant-closed"></i>
        </div>
        <h3 class="empty-title">Belum Ada Produk</h3>
        <p class="empty-description">Produk redeem akan segera tersedia. Pantau terus halaman ini!</p>
      </div>

      <div v-else class="products-grid">
        <div v-for="product in products" :key="product.id" class="product-card">
          <div class="product-image">
            <img 
              :src="getProductImage(product)" 
              :alt="product.nama" 
              class="product-photo"
              @error="handleImageError"
            />
            <div class="product-badge">
              <span class="badge-text">{{ product.point }} Poin</span>
            </div>
          </div>
          
          <div class="product-content">
            <h4 class="product-title">{{ product.nama }}</h4>
            
            <div class="product-meta">
              <div class="product-stock">
                <i class="mdi mdi-package-variant"></i>
                <span>Stok: {{ product.stok }} {{ product.satuan }}</span>
              </div>
              <div class="product-points">
                <i class="mdi mdi-star"></i>
                <span>{{ product.point }} Poin</span>
              </div>
            </div>
          </div>
          
          <div class="product-actions">
            <bert-button 
              variant="primary"
              size="md"
              :disabled="userPoints.point < product.point || product.stok <= 0"
              @click="selectProduct(product)"
              full-width
              class="select-btn"
            >
              <span v-if="product.stok <= 0">Stok Habis</span>
              <span v-else-if="userPoints.point < product.point">Poin Tidak Cukup</span>
              <span v-else>Pilih Produk</span>
            </bert-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Redeem Form -->
    <div v-if="showForm" class="form-section">
      <div class="form-container">
        <div class="selected-product-info">
          <h4>Produk Terpilih: {{ forms.produk }}</h4>
          <p>Poin yang dibutuhkan: {{ forms.point }}</p>
        </div>

        <form @submit.prevent="submitData" class="redeem-form">
          <div class="form-grid">
            <!-- Product Info (Hidden) -->
            <input type="hidden" v-model="forms.produk_id" />
            <input type="hidden" v-model="forms.produk" />
            <input type="hidden" v-model="forms.point" />

            <!-- Name Field -->
            <div class="form-group">
              <label class="form-label">Nama Lengkap :</label>
              <div class="input-wrapper">
                <input
                  type="text"
                  v-model="forms.nama_lengkap"
                  class="form-input"
                  required
                  placeholder="Masukkan nama lengkap"
                />
                <i class="mdi mdi-account input-icon"></i>
              </div>
            </div>

            <!-- Phone Field -->
            <div class="form-group">
              <label class="form-label">No. Handphone :</label>
              <div class="input-wrapper">
                <input
                  type="tel"
                  v-model="forms.wa"
                  class="form-input"
                  required
                  placeholder="Masukkan nomor handphone"
                />
                <i class="mdi mdi-phone input-icon"></i>
              </div>
            </div>

            <!-- Address Field -->
            <div class="form-group full-width">
              <label class="form-label">Alamat Lengkap :</label>
              <div class="input-wrapper">
                <textarea
                  v-model="forms.alamat_lengkap"
                  class="form-textarea"
                  required
                  placeholder="Masukkan alamat lengkap"
                  rows="3"
                ></textarea>
                <i class="mdi mdi-map-marker input-icon"></i>
              </div>
            </div>

            <!-- Province Field -->
            <div class="form-group">
              <label class="form-label">Provinsi :</label>
              <div class="select-wrapper">
                <select
                  v-model="modelProvinsi"
                  @change="loadKota(modelProvinsi.kode)"
                  class="form-select"
                  required
                >
                  <option value="">Pilih Provinsi</option>
                  <option v-for="item in provinsi" :value="item" :key="item.kode">
                    {{ item.nama }}
                  </option>
                </select>
                <i class="mdi mdi-chevron-down select-icon"></i>
              </div>
            </div>

            <!-- City Field -->
            <div class="form-group">
              <label class="form-label">Kota :</label>
              <div class="select-wrapper">
                <select
                  v-model="modelKota"
                  @change="loadKecamatan(modelKota.kode)"
                  class="form-select"
                  required
                  :disabled="!modelProvinsi"
                >
                  <option value="">Pilih Kota</option>
                  <option v-for="item in kota" :value="item" :key="item.kode">
                    {{ item.nama }}
                  </option>
                </select>
                <i class="mdi mdi-chevron-down select-icon"></i>
              </div>
            </div>

            <!-- District Field -->
            <div class="form-group">
              <label class="form-label">Kecamatan :</label>
              <div class="select-wrapper">
                <select
                  v-model="modelKecamatan"
                  @change="loadKelurahan(modelKecamatan.kode)"
                  class="form-select"
                  required
                  :disabled="!modelKota"
                >
                  <option value="">Pilih Kecamatan</option>
                  <option v-for="item in kecamatan" :value="item" :key="item.kode">
                    {{ item.nama }}
                  </option>
                </select>
                <i class="mdi mdi-chevron-down select-icon"></i>
              </div>
            </div>

            <!-- Sub-district Field -->
            <div class="form-group">
              <label class="form-label">Kelurahan :</label>
              <div class="select-wrapper">
                <select
                  v-model="modelKelurahan"
                  class="form-select"
                  required
                  :disabled="!modelKecamatan"
                >
                  <option value="">Pilih Kelurahan</option>
                  <option v-for="item in kelurahan" :value="item" :key="item.kode">
                    {{ item.nama }}
                  </option>
                </select>
                <i class="mdi mdi-chevron-down select-icon"></i>
              </div>
            </div>

            <!-- Postal Code Field -->
            <div class="form-group">
              <label class="form-label">Kode POS :</label>
              <div class="input-wrapper">
                <input
                  type="text"
                  v-model="forms.kode_pos"
                  class="form-input"
                  required
                  placeholder="Masukkan kode pos"
                />
                <i class="mdi mdi-mailbox input-icon"></i>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <bert-button
              type="button"
              variant="outline"
              size="lg"
              @click="cancelForm"
              class="cancel-btn"
            >
              Batal
            </bert-button>
            <bert-button
              type="submit"
              variant="primary"
              size="lg"
              :loading="isLoading"
              :disabled="!isFormValid"
              class="submit-btn"
            >
              Submit Redeem
            </bert-button>
          </div>
        </form>

        <!-- Terms and Conditions -->
        <div v-if="detailUser.community_id !== 12 && detailUser.community_id !== 13" class="terms-section">
          <div class="terms-box">
            <h4 class="terms-title">Syarat dan Ketentuan:</h4>
            <ol class="terms-list">
              <li>Produk akan dikirim setiap 2 minggu sekali menyesuaikan tim pengiriman barang & dihubungi oleh Admin.</li>
              <li>Bersedia membuat konten sekreatif mungkin sesuai yang diinfokan Admin.</li>
              <li>Deadline konten H+10 setelah barang sampai & Jika konten sudah melewati deadline maka redeem selanjutnya tidak tervalidasi.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import AppPageLayout from '@/components/ui/PageLayout.vue';
import BertButton from '@/components/ui/Button.vue';

export default {
  name: "NewRedeemProduk",
  components: {
    PageLayout: AppPageLayout,
    'bert-button': BertButton
  },
  data() {
    return {
      isLoading: false,
      showForm: false,
      userPoints: {},
      detailUser: {},
      setting: {},
      todayName: '',
      products: [],
      
      // Location data
      provinsi: [],
      kota: [],
      kecamatan: [],
      kelurahan: [],
      modelProvinsi: '',
      modelKota: '',
      modelKecamatan: '',
      modelKelurahan: '',
      
      // Form data
      forms: {
        produk_id: '',
        produk: '',
        point: '',
        nama_lengkap: '',
        wa: '',
        alamat_lengkap: '',
        kode_pos: ''
      },
      
      errors: []
    };
  },
  computed: {
    isExchangeAvailable() {
      if (!this.setting.hari_redeem && !this.setting.hari_redeem2) {
        return false;
      }
      return this.setting.hari_redeem === this.todayName || 
             this.setting.hari_redeem2 === this.todayName;
    },
    
    isFormValid() {
      return this.forms.nama_lengkap && 
             this.forms.wa && 
             this.forms.alamat_lengkap &&
             this.modelProvinsi &&
             this.modelKota &&
             this.modelKecamatan &&
             this.modelKelurahan &&
             this.forms.kode_pos;
    }
  },
  methods: {
    goBack() {
      if (this.showForm) {
        this.cancelForm();
        this.$router.go(-1);
      } else {
        this.$router.go(-1);
      }
    },
    
    goToHistory() {
      this.$router.push('/user/redeem-history');
    },
    
    getExchangeDays() {
      const days = [];
      if (this.setting.hari_redeem) days.push(this.setting.hari_redeem);
      if (this.setting.hari_redeem2) days.push(this.setting.hari_redeem2);
      return days.join(' dan ') || 'Belum ditentukan';
    },
    
    selectProduct(product) {
      if (this.userPoints.point < product.point) {
        this.$swal({
          title: 'Poin Tidak Cukup',
          text: `Kamu membutuhkan ${product.point} poin untuk menukar produk ini. Poin kamu saat ini: ${this.userPoints.point}`,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      if (product.stok <= 0) {
        this.$swal({
          title: 'Stok Habis',
          text: 'Maaf, produk ini sedang tidak tersedia.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      // Set selected product
      this.forms.produk_id = product.id;
      this.forms.produk = product.nama;
      this.forms.point = product.point;
      
      // Show form
      this.showForm = true;
    },
    
    cancelForm() {
      this.showForm = false;
      this.resetForm();
    },
    
    resetForm() {
      this.forms = {
        produk_id: '',
        produk: '',
        point: '',
        nama_lengkap: '',
        wa: '',
        alamat_lengkap: '',
        kode_pos: ''
      };
      this.modelProvinsi = '';
      this.modelKota = '';
      this.modelKecamatan = '';
      this.modelKelurahan = '';
      this.kota = [];
      this.kecamatan = [];
      this.kelurahan = [];
      this.errors = [];
    },
    
    submitData() {
      if (!this.isFormValid) return;
      
      this.isLoading = true;
      let formData = new FormData();
      
      formData.append("produk", this.forms.produk);
      formData.append("produk_id", this.forms.produk_id);
      formData.append("point", this.forms.point);
      formData.append("nama_lengkap", this.forms.nama_lengkap);
      formData.append("wa", this.forms.wa);
      formData.append("alamat_lengkap", this.forms.alamat_lengkap);
      formData.append("provinsi", this.modelProvinsi.nama);
      formData.append("kota", this.modelKota.nama);
      formData.append("kecamatan", this.modelKecamatan.nama);
      formData.append("kelurahan", this.modelKelurahan.nama);
      formData.append("kode_pos", this.forms.kode_pos);

      const baseURI = this.$settings.endPoint + "redeem-produk/create";
      this.$http
        .post(baseURI, formData, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Berhasil melakukan redeem produk!');
            this.resetForm();
            this.showForm = false;
            // Refresh user points
            this.loadUserPoints();
            // Redirect to history after success
            setTimeout(() => {
              this.$router.push('/user/redeem-history');
            }, 1500);
          } else {
            this.handleErrors(response.data.errors);
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    // Location loading methods
    loadProvinsi() {
      const baseURI = this.$settings.endPoint + "daerah/select-provinsi";
      
      return this.$http
        .get(baseURI, { timeout: 100000 })
        .then((response) => {
          this.provinsi = response.data.datas || [];
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    loadKota(kode_provinsi) {
      if (!kode_provinsi) return;
      
      const baseURI = this.$settings.endPoint + "daerah/select-kota/" + kode_provinsi;
      
      return this.$http
        .get(baseURI, { timeout: 100000 })
        .then((response) => {
          this.kota = response.data.datas || [];
          // Reset dependent fields
          this.modelKota = '';
          this.modelKecamatan = '';
          this.modelKelurahan = '';
          this.kecamatan = [];
          this.kelurahan = [];
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    loadKecamatan(kode_kota) {
      if (!kode_kota) return;
      
      const baseURI = this.$settings.endPoint + "daerah/select-kecamatan/" + kode_kota;
      
      return this.$http
        .get(baseURI, { timeout: 100000 })
        .then((response) => {
          this.kecamatan = response.data.datas || [];
          // Reset dependent fields
          this.modelKecamatan = '';
          this.modelKelurahan = '';
          this.kelurahan = [];
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    loadKelurahan(kode_kecamatan) {
      if (!kode_kecamatan) return;
      
      const baseURI = this.$settings.endPoint + "daerah/select-kelurahan/" + kode_kecamatan;
      
      return this.$http
        .get(baseURI, { timeout: 100000 })
        .then((response) => {
          this.kelurahan = response.data.datas || [];
          // Reset dependent field
          this.modelKelurahan = '';
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    // Data loading methods
    loadUserPoints() {
      const userDatas = this.$getUserInfo();
      const userId = userDatas.sub.user_id;
      const baseURI = this.$settings.endPoint + "pengguna/detail/" + userId;
      this.isLoading = true;
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          this.userPoints = response.data.datas || {};
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    loadProducts() {
      const baseURI = this.$settings.endPoint + "master-produk/get-produk";
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.products = response.data.datas || [];
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    loadSetting() {
      const baseURI = this.$settings.endPoint + "setting/detail/1";
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.setting = response.data.datas || {};
          // Get current day name
          const today = new Date();
          const options = { weekday: 'long' };
          this.todayName = today.toLocaleDateString('en-US', options);
        })
        .catch(error => {
          this.handleError(error);
        });
    },
    
    fetchUserData() {
      const userDatas = this.$getUserInfo();
      this.detailUser = userDatas.sub || {};
    },
    
    getProductImage(product) {
      if (product.image && product.image.trim() !== '') {
        return product.image;
      }
      return require('@/assets/cta-background.png');
    },
    
    handleImageError(event) {
      event.target.src = require('@/assets/cta-background.png');
    },
    
    // Error handling
    handleErrors(errors) {
      this.errors = errors;
      if (errors) {
        Object.keys(errors).forEach(key => {
          if (errors[key] && errors[key].length > 0) {
            this.error(errors[key][0]);
          }
        });
      }
    },
    
    handleError(error) {
      if (error.response) {
        if (error.response.status === 422) {
          this.handleErrors(error.response.data.errors);
        } else if (error.response.status === 500) {
          this.$router.push('/server-error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$router.push('/page-not-found');
        }
      } else if (error.code === "ECONNABORTED") {
        this.error("Please check your internet connection");
      } else {
        this.error("An error occurred. Please try again.");
      }
    },
    
    success(message) {
      this.$swal({
        title: 'Berhasil!',
        text: message,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#F2994A'
      });
    },
    
    error(message) {
      this.$swal({
        title: 'Error!',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#F2994A'
      });
    }
  },
  
  mounted() {
    this.fetchUserData();
    this.loadUserPoints();
    this.loadProducts();
    this.loadSetting();
    this.loadProvinsi();
    
    // Check if coming from NewPoints with product selection
    if (this.$route.query.product_id) {
      const product = {
        id: this.$route.query.product_id,
        nama: this.$route.query.product_name,
        point: parseInt(this.$route.query.product_points)
      };
      this.selectProduct(product);
    }
  }
};
</script>

<style scoped>
/* ========================================= */
/* POINTS HEADER SECTION                     */
/* ========================================= */
.points-header {
  background: linear-gradient(135deg, #F2994A 0%, #F2C94C 100%);
  padding: 24px 32px;
  margin: 0 auto 32px auto;
  border-radius: 16px;
  max-width: 1200px;
  width: calc(100% - 64px);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.points-display {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.points-icon {
  font-size: 1.5rem;
}

.points-text {
  font-size: 1.2rem;
  font-weight: 700;
}

.history-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.history-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* ========================================= */
/* EXCHANGE NOTICE SECTION                   */
/* ========================================= */
.exchange-notice {
  background: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 12px;
  padding: 20px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.notice-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.notice-icon {
  font-size: 1.5rem;
  color: #F59E0B;
  margin-top: 2px;
}

.notice-text h4 {
  color: #92400E;
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.notice-text p {
  color: #92400E;
  margin: 0;
  line-height: 1.5;
}

/* ========================================= */
/* PRODUCTS SECTION                          */
/* ========================================= */
.products-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.section-header {
  text-align: center;
  margin-bottom: 32px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 8px 0;
}

.section-subtitle {
  color: #6B7280;
  margin: 0;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.product-card:hover {
  transform: translateY(-4px);
}

.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.product-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #4A90E2;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 16px 0;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  margin-top: auto;
}

.product-stock, .product-points {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6B7280;
}

.product-actions {
  padding: 0 20px 20px 20px;
}

.select-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

/* ========================================= */
/* FORM SECTION                              */
/* ========================================= */
.form-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.form-container {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.selected-product-info {
  background: #F0F9FF;
  border: 1px solid #0EA5E9;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.selected-product-info h4 {
  color: #0C4A6E;
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 700;
}

.selected-product-info p {
  color: #0C4A6E;
  margin: 0;
  font-size: 0.9rem;
}

.redeem-form {
  width: 100%;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

/* Input Styles */
.input-wrapper {
  position: relative;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #F9FAFB;
  font-family: inherit;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #F2994A;
  background: white;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 14px;
  color: #9CA3AF;
  font-size: 1.1rem;
}

/* Select Styles */
.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 12px 44px 12px 16px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #F9FAFB;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
}

.form-select:focus {
  outline: none;
  border-color: #F2994A;
  background: white;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

.form-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  font-size: 1.1rem;
  pointer-events: none;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  padding-top: 24px;
  border-top: 1px solid #E5E7EB;
}

.cancel-btn, .submit-btn {
  min-width: 150px;
}

/* ========================================= */
/* TERMS SECTION                             */
/* ========================================= */
.terms-section {
  margin-top: 32px;
}

.terms-box {
  background: #FFF7ED;
  border: 2px solid #FB923C;
  border-radius: 12px;
  padding: 20px;
}

.terms-title {
  color: #EA580C;
  margin: 0 0 16px 0;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.terms-list {
  margin: 0;
  padding-left: 20px;
  color: #9A3412;
}

.terms-list li {
  font-size: 0.9rem;
  margin-bottom: 12px;
  line-height: 1.5;
}

.terms-list li:last-child {
  margin-bottom: 0;
}

/* ========================================= */
/* EMPTY STATE                               */
/* ========================================= */
.empty-state {
  background: white;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #F3F4F6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  font-size: 2.5rem;
  color: #9CA3AF;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 8px 0;
}

.empty-description {
  color: #6B7280;
  margin: 0;
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .points-header,
  .exchange-notice,
  .products-section,
  .form-section {
    width: calc(100% - 40px);
    margin: 0 20px 24px 20px;
    padding: 16px;
  }
  
  .points-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .form-container {
    padding: 24px 20px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 24px;
  }
  
  .form-group.full-width {
    grid-column: 1;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .cancel-btn, .submit-btn {
    min-width: 100%;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .product-image {
    height: 160px;
  }
  
  .product-content {
    padding: 16px;
  }
  
  .product-actions {
    padding: 0 16px 16px 16px;
  }
  
  .notice-content {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .terms-box {
    padding: 16px;
  }
  
  .terms-list {
    padding-left: 16px;
  }
}
</style>